/**
 * Auth0 callback route. Renders a spinner while the SDK processes the redirect,
 * and shows a meaningful error screen if Auth0 returns an error (e.g. access_denied).
 */
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, LogOut } from 'lucide-react';
import { notifyLoginEvent } from '../services/tenantApi';
import { TenantAppearanceDock } from '../components/tenant/TenantAppearanceDock';

const API_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE || undefined;

export default function CallbackPage() {
  const { error, isAuthenticated, isLoading, logout, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && isAuthenticated && !error) {
      if (!notifiedRef.current) {
        notifiedRef.current = true;
        const getToken = () =>
          API_AUDIENCE
            ? getAccessTokenSilently({ authorizationParams: { audience: API_AUDIENCE } })
            : getAccessTokenSilently();
        notifyLoginEvent(getToken);
      }
      navigate('/', { replace: true });
    }
  }, [isLoading, isAuthenticated, error, navigate, getAccessTokenSilently]);

  if (error) {
    const isAccessDenied = error.message?.toLowerCase().includes('access_denied') ||
      error.message?.toLowerCase().includes('access denied');

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <TenantAppearanceDock />
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-white/10">
          <div className="bg-amber-50 px-6 py-5 flex items-center gap-3 border-b border-amber-100 dark:bg-amber-950/30 dark:border-amber-900/40">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0 dark:bg-amber-900/40">
              <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-amber-900 dark:text-amber-200">
                {isAccessDenied ? 'Access Denied' : 'Sign-In Error'}
              </h1>
              <p className="text-xs text-amber-600 dark:text-amber-400">Something went wrong during sign-in</p>
            </div>
          </div>

          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-gray-700 leading-relaxed dark:text-gray-300">
              {isAccessDenied
                ? 'Your account was not recognized. Check your phone number, or use the email sign-in option on the login page if you are outside Canada.'
                : `An error occurred during sign-in: ${error.message}`}
            </p>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2 dark:bg-white/5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide dark:text-gray-400">What you can do</p>
              <ul className="text-sm text-gray-700 space-y-1.5 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-0.5">1.</span>
                  <span>Double-check your phone number, or try email sign-in from the login page</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-0.5">2.</span>
                  <span>If you are a current tenant and cannot access, please contact us at <a href="mailto:support@amberpeak.ca" className="text-amber-700 hover:underline dark:text-amber-400">support@amberpeak.ca</a></span>
                </li>
              </ul>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3 dark:bg-white/5 dark:border-white/10">
            <button
              type="button"
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-red-700 rounded-lg hover:bg-red-800 transition-colors"
            >
              <LogOut size={14} />
              Log Out
            </button>
            <button
              type="button"
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors dark:bg-gray-800 dark:text-gray-200 dark:border-white/15 dark:hover:bg-white/5"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <TenantAppearanceDock />
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-peak-red mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Completing sign in...</p>
      </div>
    </div>
  );
}
